import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Clock,
  Facebook,
  Mail,
} from "lucide-react";
import { useSettings } from "@/lib/settings";

const PARTNERS = [
  { name: "TBI Bank", logo: "/partners/tbi.webp" },
  { name: "BT Direct", logo: "/partners/bt-direct.png" },
  { name: "UniCredit", logo: "/partners/unicredit.webp" },
  { name: "Mogo", logo: "/partners/mogo.webp" },
];

function PartnerCard({ name, logo }) {
  return (
    <div
      data-testid={`partner-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative flex items-center justify-center h-20 sm:h-24 px-6 sm:px-8"
    >
      <img
        src={logo}
        alt={`${name} logo`}
        loading="lazy"
        className="relative max-h-9 sm:max-h-11 max-w-[130px] sm:max-w-[160px] w-auto object-contain opacity-80 group-hover:opacity-100 transition-all duration-500 ease-out group-hover:scale-[1.05]"
      />
    </div>
  );
}

export default function Footer() {
  const { settings } = useSettings();

  return (
    <footer className="relative bg-[#0a0a0a] text-white border-t border-white/[0.06] overflow-hidden">
      {/* Gold accent line */}
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

      {/* Financial partners — single anthracite band */}
      <section className="border-b border-white/[0.06] py-14 sm:py-16">
        <div className="container-wide">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <div className="eyebrow">Cu încrederea instituțiilor</div>
            <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white font-display">
              Parteneri{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                financiari
              </span>
            </h3>
          </div>

          <div className="relative rounded-2xl border border-white/[0.08] bg-[#141414] overflow-hidden shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)]">
            {/* Top gold hairline */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent"
            />
            {/* Inner light showcase strip that spans full width */}
            <div className="relative m-3 sm:m-4 rounded-xl bg-[#f6f2e9] shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-black/[0.06]">
                {PARTNERS.map((p) => (
                  <PartnerCard key={p.name} {...p} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main footer */}
      <section className="py-16 sm:py-20">
        <div className="container-wide grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={settings.logo_url}
                alt="AutoRIO"
                className="h-12 w-12 rounded-full object-cover ring-1 ring-[#D4AF37]/40"
              />
              <div>
                <div className="text-xl font-extrabold tracking-tight">
                  Auto<span className="gold-text">RIO</span>
                </div>
                <div className="text-[9px] uppercase tracking-[0.28em] text-white/40 mt-1">
                  Dealer Premium · Suceava
                </div>
              </div>
            </div>
            <p className="text-sm text-white/55 leading-relaxed max-w-sm">
              Autoturisme verificate tehnic, cu istoric complet, pregătite
              pentru livrare oriunde în România. Consultanță, finanțare și
              garanție incluse.
            </p>
            <div className="mt-6 pt-6 border-t border-white/[0.06] text-[11px] text-white/40 leading-relaxed space-y-0.5">
              <div className="text-white/70 font-bold uppercase tracking-[0.18em] text-[10px] mb-1.5">
                ODI MEGA TRANS S.R.L.
              </div>
              <div>CUI: RO35133743</div>
              <div>J2015000946337</div>
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Navigare
            </div>
            <ul className="space-y-2.5 text-sm text-white/60">
              <li>
                <Link to="/" className="hover:text-white transition-colors">
                  Acasă
                </Link>
              </li>
              <li>
                <Link to="/masini" className="hover:text-white transition-colors">
                  Mașini
                </Link>
              </li>
              <li>
                <Link to="/masini-la-comanda" className="hover:text-white transition-colors">
                  Mașini la comandă
                </Link>
              </li>
              <li>
                <Link to="/finantare" className="hover:text-white transition-colors">
                  Finanțare
                </Link>
              </li>
              <li>
                <Link to="/despre" className="hover:text-white transition-colors">
                  Despre noi
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>

            <div className="mt-8 text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Legal
            </div>
            <ul className="space-y-2.5 text-sm text-white/60">
              <li>
                <Link
                  to="/politica-cookie"
                  data-testid="footer-cookie-policy"
                  className="hover:text-white transition-colors"
                >
                  Politica Cookie
                </Link>
              </li>
              <li>
                <Link
                  to="/politica-confidentialitate"
                  data-testid="footer-privacy-policy"
                  className="hover:text-white transition-colors"
                >
                  Politica de Confidențialitate
                </Link>
              </li>
              <li>
                <Link
                  to="/termeni-si-conditii"
                  data-testid="footer-terms"
                  className="hover:text-white transition-colors"
                >
                  Termeni și Condiții
                </Link>
              </li>
              <li>
                <Link
                  to="/stergere-date"
                  data-testid="footer-data-deletion"
                  className="hover:text-white transition-colors"
                >
                  Ștergere Date
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <span className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 shrink-0">
                  <Phone className="h-3.5 w-3.5 text-[#D4AF37]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-0.5">
                    Telefon
                  </div>
                  <a
                    href={`tel:${settings.phone.replace(/\s/g, "")}`}
                    className="hover:text-[#D4AF37] transition-colors font-bold block"
                  >
                    {settings.phone}
                  </a>
                  {settings.phone_secondary && (
                    <a
                      href={`tel:${settings.phone_secondary.replace(/\s/g, "")}`}
                      data-testid="footer-phone-alt"
                      className="hover:text-[#D4AF37] transition-colors font-bold block mt-1"
                    >
                      {settings.phone_secondary}
                    </a>
                  )}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 shrink-0">
                  <Mail className="h-3.5 w-3.5 text-[#D4AF37]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-0.5">
                    Email
                  </div>
                  <a
                    href={`mailto:${settings.email}`}
                    className="hover:text-[#D4AF37] transition-colors"
                  >
                    {settings.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 shrink-0">
                  <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-0.5">
                    Adresă
                  </div>
                  <span className="leading-relaxed">
                    {settings.address_street}
                    <br />
                    {settings.address_city}, {settings.address_country}
                  </span>
                </div>
              </li>
            </ul>
          </div>

          {/* Program + social */}
          <div className="md:col-span-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Program
            </div>
            <div className="flex items-start gap-3 text-sm text-white/70">
              <span className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 shrink-0">
                <Clock className="h-3.5 w-3.5 text-[#D4AF37]" />
              </span>
              <span className="whitespace-pre-line leading-relaxed">
                {settings.schedule.replace(/ · /g, "\n")}
              </span>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <a
                href={settings.facebook_url || "https://www.facebook.com/DealerAutoRIO"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 text-sm font-semibold text-white/85 hover:text-[#D4AF37] transition-colors group"
              >
                <span className="h-8 w-8 rounded-lg bg-white/[0.03] border border-white/[0.08] group-hover:border-[#D4AF37]/40 flex items-center justify-center transition-colors">
                  <Facebook className="h-4 w-4" />
                </span>
                Facebook
              </a>
              <a
                href="https://www.tiktok.com/@autoriodealer"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok-link"
                className="inline-flex items-center gap-2.5 text-sm font-semibold text-white/85 hover:text-[#D4AF37] transition-colors group"
              >
                <span className="h-8 w-8 rounded-lg bg-white/[0.03] border border-white/[0.08] group-hover:border-[#D4AF37]/40 flex items-center justify-center transition-colors">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M16.5 3c.36 2.2 1.6 3.67 3.7 3.9v2.53c-1.22.12-2.29-.28-3.53-1.03v6.36c0 4.1-3.6 6.6-7.2 5.16-2.32-.93-3.47-3.35-2.9-5.8.55-2.37 2.86-3.98 5.32-3.6v2.6c-.38-.06-.79-.09-1.18-.02-1.13.2-1.87 1.2-1.72 2.32.15 1.13 1.2 1.9 2.4 1.66 1.05-.2 1.78-1.13 1.78-2.24V3h1.53z" />
                  </svg>
                </span>
                TikTok
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SAL + SOL — ANPC compliance badges */}
      <div className="border-t border-white/[0.06]">
        <div className="container-wide py-6 flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-4 sm:gap-6">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40 hidden sm:inline">
            Conformitate
          </span>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://reclamatiisal.anpc.ro/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-anpc-sal"
              aria-label="ANPC — Soluționarea Alternativă a Litigiilor"
              className="inline-flex bg-white/[0.03] hover:bg-white/[0.06] rounded-lg p-1.5 border border-white/[0.06] hover:border-[#D4AF37]/40 transition-colors"
            >
              <img
                src="/legal/anpc-sal.webp"
                alt="ANPC — Soluționarea Alternativă a Litigiilor"
                loading="lazy"
                className="h-11 sm:h-12 w-auto"
              />
            </a>
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-anpc-sol"
              aria-label="Soluționarea Online a Litigiilor"
              className="inline-flex bg-white/[0.03] hover:bg-white/[0.06] rounded-lg p-1.5 border border-white/[0.06] hover:border-[#D4AF37]/40 transition-colors"
            >
              <img
                src="/legal/sol.webp"
                alt="Soluționarea Online a Litigiilor"
                loading="lazy"
                className="h-11 sm:h-12 w-auto"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/[0.06]">
        <div className="container-wide py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-white/40">
          <div>
            © {new Date().getFullYear()} AutoRIO · ODI MEGA TRANS S.R.L. Toate
            drepturile rezervate.
          </div>
          <div className="flex items-center gap-5">
            <span>
              Realizat de{" "}
              <a
                href="https://wa.me/40754482899"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-author-link"
                className="font-semibold text-white hover:text-[#D4AF37] transition-colors"
              >
                Alexandru
              </a>
            </span>
            <Link
              to="/admin/login"
              className="hover:text-white transition-colors"
              data-testid="footer-admin-link"
            >
              Panou administrator
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
